import { type ChangeEvent, useCallback, useMemo, useState } from 'react';
import type { TradingStrategy } from '@einhaus/project314-types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import UserLayout from '@/components/Layout/UserLayout';
import { Button, Callout, EmptyState, FormCheckbox, FormSearchInput, SegmentedControl } from '@/components/UI';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { showToast } from '@/components/Layout/Toast';
import { trpc } from '@/api/trpc';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useTradingProfileDirectory } from '@/hooks/useTradingProfileDirectory';
import AddStrategyModal, { type AddStrategyPortfolio } from '@/pages/User/PortfolioSettings/components/AddStrategyModal';
import LiveStrategyDeactivationDialog from '@/pages/User/PortfolioSettings/components/LiveStrategyDeactivationDialog';
import {
    buildConfigureStrategySubscriptionPath,
    buildStrategySubscriptionFilterOptions,
    buildStrategySubscriptionRows,
    countStrategySubscriptionStatuses,
    describeStrategySubscriptionCounts,
    matchesStrategySubscriptionFilter,
    type StrategySubscriptionFilter,
    type StrategySubscriptionRow
} from '@/pages/User/PortfolioSettings/components/strategySubscription';
import { useStrategySubscriptionActivation } from '@/pages/User/PortfolioSettings/components/useStrategySubscriptionActivation';
import StrategySubscriptionGroupCard from './components/StrategySubscriptionGroupCard';
import {
    buildPortfolioTypeFilterOptions,
    describeSubscriptionReach,
    GROUPING_PARAM,
    groupStrategySubscriptionRows,
    isInactiveStrategySubscription,
    isSwitchedOnButNotTrading,
    matchesPortfolioTypeFilter,
    matchesStrategySubscriptionSearch,
    parseSearchTerms,
    parseStrategySubscriptionGrouping,
    SHOW_INACTIVE_STRATEGIES_PARAM,
    type PortfolioTypeFilter,
    type StrategySubscriptionGrouping
} from './components/strategySubscriptionGroups';

const PORTFOLIOS_REFETCH_INTERVAL_MS = 30_000;

const NO_PORTFOLIOS: AddStrategyPortfolio[] = [];

/** Wide enough that long strategy and trading profile names wrap to a line or two instead of four or more. */
const PAGE_CONTAINER_SIZE = '1600px';

const GROUPING_OPTIONS: { value: StrategySubscriptionGrouping; label: string }[] = [
    { value: 'portfolio', label: 'By portfolio' },
    { value: 'strategy', label: 'By strategy' }
];

/** Every strategy subscription across all of the user's portfolios, with each portfolio's on/off switch. */
const StrategySubscriptions = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const grouping = parseStrategySubscriptionGrouping(searchParams.get(GROUPING_PARAM));
    const showInactiveStrategies = searchParams.get(SHOW_INACTIVE_STRATEGIES_PARAM) === 'true';

    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [portfolioTypeFilter, setPortfolioTypeFilter] = useState<PortfolioTypeFilter>('all');
    const [statusFilter, setStatusFilter] = useState<StrategySubscriptionFilter>('all');
    /** The portfolio the add-strategy dialog starts from; `null` lets the user pick. `undefined` while it is closed. */
    const [addStrategyPortfolioId, setAddStrategyPortfolioId] = useState<number | null | undefined>(undefined);

    const portfoliosQuery = useAppQuery(
        {
            ...trpc.user.fetchPortfolios.queryOptions(),
            refetchInterval: PORTFOLIOS_REFETCH_INTERVAL_MS,
            // Keep the previous data while refetching so the tables do not flicker
            placeholderData: (previousData) => previousData
        },
        {
            fallbackErrorMessage: 'Failed to load portfolios',
            setError
        }
    );

    const { directory: tradingProfileDirectory, isLoaded: areTradingProfilesLoaded } = useTradingProfileDirectory({ setError });

    const handleConfigure = useCallback(
        (row: StrategySubscriptionRow) => {
            void navigate(buildConfigureStrategySubscriptionPath(row.portfolio.id, row.strategy.id));
        },
        [navigate]
    );

    const { activationState, cancelDeactivation, confirmDeactivation, pendingDeactivationRow, requestToggle } =
        useStrategySubscriptionActivation({ onDeploymentIncomplete: handleConfigure });

    const rows = useMemo<StrategySubscriptionRow[]>(
        () =>
            (portfoliosQuery.data?.portfolios ?? []).flatMap((portfolio) =>
                buildStrategySubscriptionRows(
                    { portfolio, portfolioPositionSizeRules: portfolio.settings?.positionSizeRules ?? [] },
                    tradingProfileDirectory,
                    activationState
                )
            ),
        [activationState, portfoliosQuery.data?.portfolios, tradingProfileDirectory]
    );

    const inactiveStrategyRowCount = useMemo(() => rows.filter(isInactiveStrategySubscription).length, [rows]);

    const shownRows = useMemo(
        () => (showInactiveStrategies ? rows : rows.filter((row) => !isInactiveStrategySubscription(row))),
        [rows, showInactiveStrategies]
    );

    // Each control's counts reflect the controls before it, so every option says how many rows it would show.
    const searchedRows = useMemo(() => {
        const terms = parseSearchTerms(search);

        return shownRows.filter((row) => matchesStrategySubscriptionSearch(row, terms));
    }, [search, shownRows]);

    const portfolioTypeOptions = useMemo(() => buildPortfolioTypeFilterOptions(searchedRows), [searchedRows]);

    const scopedRows = useMemo(
        () => searchedRows.filter((row) => matchesPortfolioTypeFilter(row, portfolioTypeFilter)),
        [portfolioTypeFilter, searchedRows]
    );

    const scopedStatusCounts = useMemo(() => countStrategySubscriptionStatuses(scopedRows), [scopedRows]);

    const statusOptions = useMemo(
        () => buildStrategySubscriptionFilterOptions(scopedRows.length, scopedStatusCounts),
        [scopedRows.length, scopedStatusCounts]
    );

    const groups = useMemo(
        () =>
            groupStrategySubscriptionRows(
                scopedRows.filter((row) => matchesStrategySubscriptionFilter(row.readiness.status, statusFilter)),
                grouping
            ),
        [grouping, scopedRows, statusFilter]
    );

    const summary = useMemo(
        () =>
            shownRows.length === 0
                ? null
                : `${describeStrategySubscriptionCounts(shownRows.length, countStrategySubscriptionStatuses(shownRows))} · ${describeSubscriptionReach(shownRows)}`,
        [shownRows]
    );

    // Never hidden: a switched-on subscription is not an inactive-strategy subscription, whatever the hide option says.
    const notTradingRows = useMemo(() => rows.filter(isSwitchedOnButNotTrading), [rows]);

    const setSearchParam = useCallback(
        (name: string, value: string | null) => {
            setSearchParams(
                (currentParams) => {
                    const nextParams = new URLSearchParams(currentParams);

                    if (value === null) {
                        nextParams.delete(name);
                    } else {
                        nextParams.set(name, value);
                    }

                    return nextParams;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );

    const handleGroupingChange = useCallback(
        (nextGrouping: StrategySubscriptionGrouping) => setSearchParam(GROUPING_PARAM, nextGrouping),
        [setSearchParam]
    );

    const handleHideInactiveStrategiesChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => setSearchParam(SHOW_INACTIVE_STRATEGIES_PARAM, event.target.checked ? null : 'true'),
        [setSearchParam]
    );

    const handleAddStrategy = useCallback((portfolioId: number | null) => setAddStrategyPortfolioId(portfolioId), []);

    const handleCloseAddStrategy = useCallback(() => setAddStrategyPortfolioId(undefined), []);

    const handleStrategyAdded = useCallback(
        ({ portfolio, strategy }: { portfolio: AddStrategyPortfolio; strategy: TradingStrategy }) => {
            // A new subscription starts switched off, so an inactive strategy lands straight in the hidden set.
            if (!strategy.active && !showInactiveStrategies) {
                showToast(
                    `${strategy.name} was added to ${portfolio.name}, but it is hidden because "Hide inactive strategies" is on.`,
                    'info'
                );
            }
        },
        [showInactiveStrategies]
    );

    const isLoading = portfoliosQuery.status === 'pending' || !areTradingProfilesLoaded;

    return (
        <UserLayout containerSize={PAGE_CONTAINER_SIZE} title="Strategy Subscriptions - Project 314">
            <div className="container mx-auto mt-3 space-y-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <h3 className="text-2xl font-bold text-white">Strategy Subscriptions</h3>
                        <p className="mt-1 text-sm text-zinc-400">
                            {summary ?? 'Every strategy subscribed to your portfolios, in one place.'}
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button disabled={isLoading} variant="primary" onClick={() => handleAddStrategy(null)}>
                            + Add subscription
                        </Button>
                        <Button to="/user/portfolios" variant="outline-dark">
                            Portfolios
                        </Button>
                    </div>
                </div>

                {error ? (
                    <div className="rounded-md border border-red-200 bg-red-100 p-4 text-red-800">{error}</div>
                ) : isLoading ? (
                    <DataLoadingSpinner text="Loading subscriptions..." />
                ) : rows.length === 0 ? (
                    <EmptyState
                        action={
                            <Button size="sm" variant="primary" onClick={() => handleAddStrategy(null)}>
                                + Add subscription
                            </Button>
                        }
                        description="Add a strategy to one of your portfolios and it will show up here."
                        title="None of your portfolios have strategy subscriptions yet."
                    />
                ) : (
                    <>
                        {notTradingRows.length > 0 && (
                            <Callout
                                title={`${notTradingRows.length.toLocaleString()} switched-on ${notTradingRows.length === 1 ? 'subscription is' : 'subscriptions are'} not trading`}
                                variant="warning"
                            >
                                <ul className="ml-4 list-disc space-y-0.5">
                                    {notTradingRows.map((row) => (
                                        <li key={row.key}>
                                            <span className="font-medium">{row.strategy.name}</span> in {row.portfolio.name}:{' '}
                                            {row.readiness.blockingReasons.join(' ')}
                                        </li>
                                    ))}
                                </ul>
                            </Callout>
                        )}

                        <div className="space-y-3">
                            {/* Each filter strip keeps its labels on one line and scrolls sideways when a phone is too narrow for it. */}
                            <div className="flex flex-wrap items-center gap-3">
                                <div className="max-w-full overflow-x-auto">
                                    <SegmentedControl
                                        ariaLabel="Group subscriptions"
                                        className="w-max"
                                        options={GROUPING_OPTIONS}
                                        value={grouping}
                                        onChange={handleGroupingChange}
                                    />
                                </div>
                                <div className="max-w-full overflow-x-auto">
                                    <SegmentedControl
                                        ariaLabel="Portfolio type"
                                        className="w-max"
                                        options={portfolioTypeOptions}
                                        value={portfolioTypeFilter}
                                        onChange={setPortfolioTypeFilter}
                                    />
                                </div>
                                <div className="w-full sm:w-72">
                                    <FormSearchInput
                                        noMargin
                                        aria-label="Search subscriptions"
                                        placeholder="Search strategy, portfolio, or profile"
                                        value={search}
                                        onChange={(event) => setSearch(event.target.value)}
                                    />
                                </div>
                                <FormCheckbox
                                    checked={!showInactiveStrategies}
                                    id="hideInactiveStrategies"
                                    label={`Hide inactive strategies (${inactiveStrategyRowCount.toLocaleString()})`}
                                    title="Hides switched-off subscriptions to strategies that are turned off globally"
                                    onChange={handleHideInactiveStrategiesChange}
                                />
                            </div>
                            <div className="max-w-full overflow-x-auto">
                                <SegmentedControl
                                    ariaLabel="Subscription status"
                                    className="w-max"
                                    options={statusOptions}
                                    value={statusFilter}
                                    onChange={setStatusFilter}
                                />
                            </div>
                        </div>

                        {groups.length === 0 ? (
                            <EmptyState size="sm" title="No subscriptions match these filters." />
                        ) : (
                            <div className="space-y-4">
                                {groups.map((group) => (
                                    <StrategySubscriptionGroupCard
                                        group={group}
                                        key={group.key}
                                        onAddStrategy={handleAddStrategy}
                                        onConfigure={handleConfigure}
                                        onToggleActive={requestToggle}
                                    />
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>

            <LiveStrategyDeactivationDialog row={pendingDeactivationRow} onCancel={cancelDeactivation} onConfirm={confirmDeactivation} />

            {addStrategyPortfolioId !== undefined && (
                <AddStrategyModal
                    initialPortfolioId={addStrategyPortfolioId}
                    portfolios={portfoliosQuery.data?.portfolios ?? NO_PORTFOLIOS}
                    onAdded={handleStrategyAdded}
                    onClose={handleCloseAddStrategy}
                />
            )}
        </UserLayout>
    );
};

export default StrategySubscriptions;
